import { useAuth0 } from "@auth0/auth0-react";

/**
 * Custom hook that returns a `fetchWithToken` function bound to the Auth0 SDK.
 * Usage:
 *   const fetchWithToken = useFetchWithToken();
 *   await fetchWithToken(url, { method: 'POST', body: JSON.stringify(payload) });
 *
 * Also exports useEnsureValidSession() which returns an async function that
 * verifies the session by calling getAccessTokenSilently({ ignoreCache: true }).
 * If the call fails with a missing-refresh-token/consent/login error the user
 * will be logged out and the function returns false.
 */
export function useEnsureValidSession() {
  const { getAccessTokenSilently, logout } = useAuth0();

  if (typeof getAccessTokenSilently !== "function") {
    throw new Error("Auth0 not initialized: useEnsureValidSession must be used inside an Auth0Provider");
  }

  return async function ensureValidSession() {
    try {
      // Force a refresh attempt � will fail if there is no refresh token.
      await getAccessTokenSilently({ ignoreCache: true });
      return true;
    } catch (err) {
      console.error("ensureValidSession: getAccessTokenSilently failed:", err);
      const msg = err && err.message ? err.message : String(err);

      // Common error indicators where session can't be refreshed -> force logout.
      if (/Missing Refresh Token|offline_access|consent_required|login_required/i.test(msg)) {
        try {
          logout({ returnTo: window.location.origin });
        } catch (e) {
          console.error("Logout failed while handling invalid session:", e);
        }
        return false;
      }

      // For other errors, rethrow so callers can handle them.
      throw err;
    }
  };
}

export function useFetchWithToken() {
  const { getAccessTokenSilently, logout } = useAuth0();

  if (typeof getAccessTokenSilently !== "function") {
    throw new Error("Auth0 not initialized: useFetchWithToken must be used inside an Auth0Provider");
  }

  const fetchWithToken = async (input, init = {}) => {
    // Request an access token (force a refresh attempt to detect broken sessions)
    let token;
    try {
      token = await getAccessTokenSilently({ ignoreCache: true });
    } catch (err) {
      console.error("getAccessTokenSilently failed:", err);
      const msg = err && err.message ? err.message : String(err);

      // If the failure indicates missing refresh token / consent / login required,
      // force a logout to reset local auth state so the UI can't show manage pages.
      if (/Missing Refresh Token|offline_access|consent_required|login_required/i.test(msg)) {
        try {
          logout({ returnTo: window.location.origin });
        } catch (e) {
          console.error("Logout failed while handling missing refresh token:", e);
        }
        throw new Error("Authentication state invalid; you have been logged out. Please sign in again.");
      }

      // Re-throw other errors so callers can decide what to do.
      throw err;
    }

    const headers = new Headers(init.headers || {});
    headers.set("Authorization", `Bearer ${token}`);

    // Only set JSON content-type when body is not FormData and not already set
    if (!headers.has("Content-Type") && !(init.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }

    const cfg = {
      ...init,
      headers
    };

    return fetch(input, cfg);
  };

  return fetchWithToken;
}

export default useFetchWithToken;